import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { CircularProgress, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BoxedImage, ImageBox } from '../../../components/BoxedImage';
import { ImageItemDetailsDialog } from '../../../components/ImageItemDetailsDialog';
import { LayersColumn } from '../../../components/LayersColumn';
import { DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG } from '../../../constants/planogramComplianceConfig';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import { ApiService } from '../../../services/api';
import {
  CameraDetails,
  CameraImageResponse,
  CameraPog,
  PlanogramComplianceBox,
  PlanogramComplianceConfig,
  PlanogramComplianceResponse,
  PogBBox,
  ProductRecognitionBBox,
  ProductRecognitionCropScanUpdate,
  ProductRecognitionLayerType,
  ProductRecognitionPolygon,
  ProductRecognitionResponse,
  ShapeType,
  TaskExtended,
} from '../../../types';
import { getFilteredBoxesForLayer, LayerType } from '../../../utils/get-filtered-bboxex-for-layer';
import { getPlanogramComplianceActions } from '../../../utils/planogramComplianceActions';
import { useProductRecognitionScanning } from '../../embeddings/utils/useProductRecognitionScanning';
import { PlanogramComplianceActionContent } from '../../store-details/components/PlanogramComplianceActionContent';
import { PlanogramComplianceAdvancedSettings } from '../../store-details/components/PlanogramComplianceAdvancedSettings';
import {
  ACTION_LAYER_CONFIG,
  COMPLIANCE_COLOR,
} from '../../store-details/components/PlanogramComplianceControlCenter';
import { PlanogramComplianceToggles } from '../../store-details/components/PlanogramComplianceToggles';
import { LAYER_CONFIG } from '../../store-details/components/ProductRecognitionControlCenter';
import { CameraImageViewer } from '../components/CameraImageViewer';
import { ImageAuditHeader } from '../components/ImageAuditHeader';
import { OldVersionWarningBanner } from '../components/OldVersionWarningBanner';
import { TasksHeader } from '../components/TasksHeader';
import { useTaskNavigation } from '../hooks/useTaskNavigation';
import { groupAndFilterTasks } from '../utils/taskGrouping';

const PRODUCT_RECOGNITION_LAYER_CONFIG = LAYER_CONFIG.map((config) => {
  return {
    ...config,
    enabledByDefault: ['productBoxes', 'cartonBoxes'].includes(config.key),
  };
});

const detectionTypePerLayerType: Partial<Record<ProductRecognitionLayerType, ShapeType>> = {
  cartonBoxes: ShapeType.CARTON,
  priceTagBoxes: ShapeType.PRICE_TAG,
  productBoxes: ShapeType.PRODUCT,
  shelfBoxes: ShapeType.SHELF_AREA,
  templateBoxes: ShapeType.TEMPLATE_BOX,
  templateBoxesUp: ShapeType.TEMPLATE_BOX_UP,
};

export interface AuditProps {
  allTasks?: TaskExtended[];
  imageId: string | null;
  isLoading: boolean;
  productRecognitionData: ProductRecognitionResponse | null;
  planogramComplianceData: PlanogramComplianceResponse | null;
  onProductRecognitionDataChange: (data: ProductRecognitionResponse) => void;
  onPlanogramComplianceDataChange: (data: PlanogramComplianceResponse) => void;
  onAllTasksChange?: (tasks: TaskExtended[]) => void;
}

export const Audit: React.FC<AuditProps> = ({
  allTasks = [],
  imageId,
  isLoading,
  productRecognitionData,
  planogramComplianceData,
  onAllTasksChange,
  onProductRecognitionDataChange,
  onPlanogramComplianceDataChange,
}) => {
  const { taskId } = useParams<{ taskId: string }>();
  const [searchParams] = useSearchParams();
  const cameraId = searchParams.get('cameraId');
  const chainId = searchParams.get('chain_id');
  const storeName = searchParams.get('store_name');
  const storeId = searchParams.get('store_id');
  const navigate = useNavigate();
  const { token, legacyToken } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const { createEmbeddings, startSession } = useProductRecognitionScanning(imageId || '');
  const { nextTaskUrl, deactivateTaskInLocalStorage } = useTaskNavigation({
    taskId,
    chainId,
    imageId,
  });
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
  useState<PlanogramComplianceResponse | null>(null);
  const [cameraImage, setCameraImage] = useState<CameraImageResponse | null>(null);

  const [, setIsLoadingCameraImage] = useState(true);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [enabledMods, setEnabledMods] = useState<Set<string>>(new Set());
  const [selectedDetections, setSelectedDetections] = useState<ImageBox[]>([]);
  const [detectionForModal, setDetectionForModal] = useState<
    ImageBox | { item: PogBBox; color?: string } | null
  >(null);
  const [selectionType, setSelectionType] = useState<'pog' | 'realogram'>('realogram');
  const [detectionPogPairings, setDetectionPogPairings] = useState<
    Map<
      string,
      {
        detection: ImageBox;
        upc: string;
        name?: string;
        imageUri?: string;
        source: 'pog' | 'manual';
        pogBox?: PogBBox; // Only present if source is 'pog'
        timestamp: Date;
      }
    >
  >(new Map());
  const [selectedPairingKeys, setSelectedPairingKeys] = useState<Set<string>>(new Set());
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [selectedIlItemIds, setSelectedIlItemIds] = useState<Set<number>>(new Set());
  const [planogramComplianceBoxClicked, setPlanogramComplianceBoxClicked] =
    useState<PlanogramComplianceBox | null>(null);

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [selectedShape, setSelectedShape] = useState<ImageBox | null>(null);
  const [selectedDetectionIndex, setSelectedDetectionIndex] = useState<number | null>(null);
  const [previousEnabledLayers, setPreviousEnabledLayers] = useState<Set<string>>(new Set());
  const [editedProductRecognitionData, setEditedProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  // Temporary state for drag operations (visual feedback without committing to editedProductRecognitionData)
  const [draggedDetection, setDraggedDetection] = useState<ImageBox | null>(null);
  const isTaskAuditMode = useMemo(() => !!taskId, [taskId]);

  const selectedCamera = useMemo(() => {
    if (!cameraId || !cameras || cameras.length === 0) {
      return null;
    }
    return cameras.find((cam) => cam.cameraId === parseInt(cameraId, 10)) ?? null;
  }, [cameraId, cameras]);

  const allTagNames = useMemo(() => {
    if (!selectedCamera) return [];
    return selectedCamera.mods.map((mod) => mod.tagName);
  }, [selectedCamera]);
  console.log('selectedCamera', selectedCamera);
  console.log('allTagNames', allTagNames);
  console.log('cameras', cameras);
  console.log('cameraId', cameraId);

  // Group, filter, and sort tasks
  const { sortedTasks, showWarning, newestTask } = useMemo(() => {
    const currentTaskId = taskId ? parseInt(taskId, 10) : null;

    // Apply grouping and filtering
    const { filteredTasks, showWarning, newestTask } = currentTaskId
      ? groupAndFilterTasks(allTasks, currentTaskId)
      : { filteredTasks: allTasks, showWarning: false, newestTask: null };

    // Sort filtered tasks by tagName (existing sort logic)
    if (allTagNames.length === 0) {
      return { sortedTasks: filteredTasks, showWarning, newestTask };
    }

    const sorted = [...filteredTasks].sort((a, b) => {
      if (!a.tagName || !b.tagName) return 0;
      return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
    });

    return { sortedTasks: sorted, showWarning, newestTask };
  }, [allTasks, allTagNames, taskId]);

  const getOtherActiveTaskIds = useCallback(
    (taskIds: Set<number>): Set<number> => {
      return new Set(
        sortedTasks
          .filter((task) => task.isActive && !taskIds.has(task.actionTaskId))
          .map((task) => task.actionTaskId)
      );
    },
    [sortedTasks]
  );

  const cancelTasks = useCallback(
    async (
      taskIdsToCancel: Set<number>
    ): Promise<{
      successCount: number;
      totalCount: number;
    }> => {
      const totalCount = taskIdsToCancel.size;

      if (!storeName || !onAllTasksChange || taskIdsToCancel.size === 0) {
        return {
          successCount: 0,
          totalCount,
        };
      }

      // Use Promise.allSettled to handle partial failures
      const cancelResults = await Promise.allSettled(
        [...taskIdsToCancel].map((taskIdToCancel) =>
          ApiService.closeTask(legacyToken, storeName, taskIdToCancel.toString(), 'REMOVED').then(
            () => taskIdToCancel
          )
        )
      );

      const canceledTaskIds: number[] = [];

      cancelResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          canceledTaskIds.push(result.value);
        }
      });

      // Update localStorage only for successfully canceled tasks
      const canceledTaskIdsSet = new Set(canceledTaskIds);

      canceledTaskIds.forEach((taskIdToCancel) => {
        deactivateTaskInLocalStorage(taskIdToCancel.toString());
      });

      // Update tasks state to mark canceled tasks as inactive
      onAllTasksChange(
        allTasks.map((task) =>
          canceledTaskIdsSet.has(task.taskId) ? { ...task, isActive: false } : task
        )
      );

      return {
        successCount: canceledTaskIds.length,
        totalCount,
      };
    },
    [storeName, onAllTasksChange, allTasks, legacyToken, deactivateTaskInLocalStorage]
  );

  const fetchCameraImage = useCallback(async () => {
    if (!productRecognitionData?.imageId || !storeName) {
      return null;
    }

    try {
      const imageData = await ApiService.fetchCameraImage(
        token,
        storeName,
        productRecognitionData.imageId.toString()
      );
      setCameraImage(imageData);
    } catch (err) {
      showErrorToast('Failed to fetch camera image');
      return null;
    }

    setIsLoadingCameraImage(false);
  }, [productRecognitionData?.imageId, storeName, token, showErrorToast]);

  // Get current image from cameraImage (equivalent to currentImage in SelectedCameraView)
  const currentImage = cameraImage?.images?.[0];

  // Use the image preloader hook
  const { preloadedImages, preloadedImagesB64 } = useImagePreloader({
    cameraTimeline: cameraImage,
    authToken: legacyToken,
    pogImageUri: undefined,
  });

  const preloadedImage = currentImage ? preloadedImagesB64[currentImage.imageUri] : undefined;

  const [enabledProductRecognitionLayers, setEnabledProductRecognitionLayers] = useState<
    Set<string>
  >(
    () =>
      new Set(PRODUCT_RECOGNITION_LAYER_CONFIG.filter((l) => l.enabledByDefault).map((l) => l.key))
  );
  const [enabledPlanogramComplianceLayers, setEnabledPlanogramComplianceLayers] = useState<
    Set<string>
  >(() => new Set(ACTION_LAYER_CONFIG.map((l) => l.key)));
  const [selectedComplianceLayers, setSelectedComplianceLayers] = useState<Set<string>>(
    () => new Set(Object.keys(COMPLIANCE_COLOR))
  );
  const [planogramComplianceConfig, setPlanogramComplianceConfig] =
    useState<PlanogramComplianceConfig>(DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG);
  const [isV2, setIsV2] = useState(true);
  const showProductImages = false;

  // Create a Set of detection IDs from selectedDetections for efficient lookup
  const selectedDetectionIds = useMemo(() => {
    return new Set(
      selectedDetections.filter((det) => det.shape).map((det) => det.shape!.join(','))
    );
  }, [selectedDetections]);

  const getProductRecognitionLayerCount = useCallback(
    (layerKey: string) => {
      if (!productRecognitionData) return 0;
      const layerData = productRecognitionData[layerKey as keyof ProductRecognitionResponse];
      return Array.isArray(layerData) ? layerData.length : 0;
    },
    [productRecognitionData]
  );

  const getPlanogramComplianceLayerCount = useCallback(
    (layerKey: string) => {
      return planogramComplianceData
        ? getFilteredBoxesForLayer(layerKey as LayerType, planogramComplianceData).length
        : 0;
    },
    [planogramComplianceData]
  );

  const availableProductRecognitionLayers = useMemo(() => {
    if (!editMode) {
      return PRODUCT_RECOGNITION_LAYER_CONFIG;
    }

    return PRODUCT_RECOGNITION_LAYER_CONFIG.filter(({ key }) => {
      return key.includes('Boxes') || key === 'modPolygons' || key === 'cartonPolygons';
    });
  }, [editMode]);

  const toggleProductRecognitionLayer = useCallback(
    (layerKey: string) => {
      if (editMode) {
        // In edit mode, select this layer for editing instead of toggling visibility
        setEnabledProductRecognitionLayers(new Set([layerKey]));
        // Clear any previously selected shape when changing layers
        setSelectedShape(null);
        setSelectedDetectionIndex(null);
      } else {
        // Normal behavior: toggle layer visibility
        setEnabledProductRecognitionLayers((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(layerKey)) {
            newSet.delete(layerKey);
          } else {
            newSet.add(layerKey);
          }
          return newSet;
        });
      }
    },
    [editMode]
  );

  const togglePlanogramComplianceLayer = useCallback(
    (layerKey: string) => {
      setEnabledPlanogramComplianceLayers((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(layerKey)) {
          newSet.delete(layerKey);
        } else {
          newSet.add(layerKey);
        }
        return newSet;
      });
    },
    [setEnabledPlanogramComplianceLayers]
  );

  useEffect(() => {
    fetchCameraImage();
  }, [fetchCameraImage]);

  useEffect(() => {
    if (!storeName) {
      return;
    }

    (async () => {
      const cameras = await ApiService.fetchCameras(token, storeName);
      setCameras(cameras);
    })();
  }, [storeName, token]);

  // Helper function to generate a unique key from detection shape
  const getDetectionKey = (detection: ImageBox): string => {
    if (!detection.shape) return '';
    return detection.shape.join(',');
  };

  const handlePogBoxClick = useCallback(
    (pogBox: PogBBox) => {
      // Check if we have selected detections (from left-clicks)
      if (selectedDetections.length > 0) {
        // Pair all selected detections with this POG box
        setDetectionPogPairings((prev) => {
          const newMap = new Map(prev);

          selectedDetections.forEach((detection) => {
            if (!detection.shape) return;
            const detectionKey = detection.shape.join(',');

            newMap.set(detectionKey, {
              detection,
              upc: pogBox.upc,
              name: pogBox.name || undefined,
              imageUri: undefined, // We'll use POG image from pogBox
              source: 'pog',
              pogBox: pogBox,
              timestamp: new Date(),
            });
          });

          return newMap;
        });

        // Clear selected detections
        setSelectedDetections([]);

        // Show success message
        const productInfo = [
          `UPC: ${pogBox.upc}`,
          pogBox.productId ? `Product ID: ${pogBox.productId}` : null,
          pogBox.name ? `Name: ${pogBox.name}` : null,
        ]
          .filter(Boolean)
          .join(', ');

        showSuccessToast(`${selectedDetections.length} box(es) matched with ${productInfo}`);
        return;
      }

      // Default behavior: handle ilItemId selection
      const ilItemId = pogBox?.ilItemId;
      if (ilItemId === undefined || ilItemId === null) return;

      if (selectedIlItemIds.has(ilItemId)) {
        setSelectedIlItemIds(new Set());
        setPlanogramComplianceBoxClicked(null);
      } else {
        setSelectedIlItemIds(new Set([ilItemId]));
        setPlanogramComplianceBoxClicked(null);

        setSelectionType('pog');
      }
    },
    [selectedDetections, selectedIlItemIds, showSuccessToast]
  );

  const handlePogBoxRightClick = useCallback((pogBox: ImageBox | PogBBox) => {
    setDetectionForModal({ item: pogBox as PogBBox });
    setShowDetectionDialog(true);
  }, []);

  const onPlanogramComplianceClick = useCallback(
    (imageBox: ImageBox) => {
      // Extract the PlanogramComplianceBox from the ImageBox.item
      const detection = imageBox.item as PlanogramComplianceBox;
      if (!detection) return;

      // For realogram clicks, use rogItemId to select that specific item
      const rogItemId = detection?.rogItemId;
      if (rogItemId === undefined || rogItemId === null) return;

      // Toggle logic:
      if (selectedIlItemIds.has(rogItemId)) {
        setSelectedIlItemIds(new Set()); // Deselect if already selected
        setPlanogramComplianceBoxClicked(null);
      } else {
        // Realogram click: Select by rogItemId, and also add pogItemId for planogram highlighting
        const pogItemId = detection?.pogItemId;
        const idsToSelect = new Set([rogItemId]);
        if (pogItemId !== undefined && pogItemId !== null) {
          idsToSelect.add(pogItemId);
        }
        setSelectedIlItemIds(idsToSelect);
        setPlanogramComplianceBoxClicked(detection);
        setSelectionType('realogram'); // This is a realogram box click
      }
    },
    [selectedIlItemIds]
  );

  const handleManualUpcSubmit = useCallback(
    (upc: string) => {
      if (!detectionForModal) {
        showErrorToast('Please select a detection box on the camera image first.');
        return;
      }

      const seletecDetection = detectionForModal as ImageBox;
      const detectionKey = getDetectionKey(seletecDetection);

      // Check if UPC matches any vizSimCandidates
      const matchingCandidate = seletecDetection.item?.vizSimCandidates?.find(
        (candidate) => candidate.normalizedUpc === upc
      );

      // Store the pairing
      setDetectionPogPairings((prev) => {
        const newMap = new Map(prev);
        newMap.set(detectionKey, {
          detection: seletecDetection,
          upc: upc,
          name: matchingCandidate?.productName,
          imageUri: matchingCandidate?.productImageUri,
          source: 'manual',
          timestamp: new Date(),
        });
        return newMap;
      });

      // Show success message
      const productInfo = matchingCandidate
        ? `${matchingCandidate.productName || ''} (${upc})`
        : upc;

      // Close the dialog
      setShowDetectionDialog(false);
      setDetectionForModal(null);
      showSuccessToast(`Detection matched with ${productInfo}`);
    },
    [detectionForModal, showErrorToast, showSuccessToast]
  );

  const applyPairingsToProductRecognitionData =
    useCallback((): ProductRecognitionResponse | null => {
      if (!productRecognitionData) return null;

      // Create a deep copy of productRecognitionData
      const modifiedProductRecognitionData: ProductRecognitionResponse = JSON.parse(
        JSON.stringify(productRecognitionData)
      );

      // Helper function to get detection key
      const getShapeKey = (shape: any) => {
        if (!shape || !Array.isArray(shape)) return '';
        return shape.join(',');
      };

      Object.keys(modifiedProductRecognitionData).forEach((layerKey) => {
        const layer = modifiedProductRecognitionData[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = getShapeKey(detection.shape);
              const pairing = detectionPogPairings.get(shapeKey);

              if (pairing) {
                // Update detection with pairing data
                detection.confidence = 1;
                detection.finalUpc = pairing.upc;

                // Update or add vizSimCandidates
                if (!detection.vizSimCandidates) {
                  detection.vizSimCandidates = [];
                }

                // Find if there's already a candidate with matching UPC
                const existingCandidateIndex = detection.vizSimCandidates.findIndex(
                  (candidate: any) => candidate.normalizedUpc === pairing.upc
                );

                if (existingCandidateIndex !== -1) {
                  // Update existing candidate
                  detection.vizSimCandidates[existingCandidateIndex].isConfident = true;
                  detection.vizSimCandidates[existingCandidateIndex].similarity = 1;

                  // Sort vizSimCandidates by similarity descending
                  detection.vizSimCandidates.sort(
                    (a: any, b: any) => (b.similarity || 0) - (a.similarity || 0)
                  );
                } else {
                  // Add new candidate
                  detection.vizSimCandidates.unshift({
                    normalizedUpc: pairing.upc,
                    similarity: 1,
                    isConfident: true,
                    productName: pairing.name || '',
                    itemNumber: '',
                    productImageUri: pairing.imageUri || pairing.pogBox?.imageUri || '',
                    productId: undefined,
                  });
                }
              }
            }
          });
        }
      });

      return modifiedProductRecognitionData;
    }, [productRecognitionData, detectionPogPairings]);

  const handlePreviewChanges = useCallback(async () => {
    if (!productRecognitionData || !token) {
      showErrorToast('Missing task data or authentication token');
      return;
    }

    const modifiedProductRecognitionData = applyPairingsToProductRecognitionData();

    if (!modifiedProductRecognitionData) {
      showErrorToast('Failed to prepare task data');
      return;
    }

    setIsPreviewLoading(true);

    const payload = {
      ...modifiedProductRecognitionData,
      ...(!isTaskAuditMode && { config: planogramComplianceConfig }),
    };

    try {
      const result = await ApiService.createPlanogramComplianceFromPayload(
        token,
        payload,
        !isTaskAuditMode ? isV2 : undefined
      );

      onProductRecognitionDataChange(modifiedProductRecognitionData);
      onPlanogramComplianceDataChange(result);
      showSuccessToast('Preview created successfully');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to create preview');
    } finally {
      setIsPreviewLoading(false);
    }
  }, [
    productRecognitionData,
    token,
    applyPairingsToProductRecognitionData,
    isTaskAuditMode,
    planogramComplianceConfig,
    showErrorToast,
    isV2,
    onProductRecognitionDataChange,
    onPlanogramComplianceDataChange,
    showSuccessToast,
  ]);

  const createEmbeddingsFromPairings = useCallback(async () => {
    if (!token || !imageId) {
      return;
    }

    if (selectedPairingKeys.size === 0) {
      return;
    }

    try {
      const cropScans = await startSession();
      const cropsToUpdate: ProductRecognitionCropScanUpdate[] = [];

      cropScans.forEach((cropScan) => {
        const bboxKey = cropScan.bbox.join(',');
        const pairing = detectionPogPairings.get(bboxKey);

        // Only create embeddings for selected pairings
        if (pairing && selectedPairingKeys.has(bboxKey)) {
          cropsToUpdate.push({
            product_crop_scan_id: cropScan.product_crop_scan_id,
            barcode: pairing.upc,
            normalized_upc: pairing.upc,
            product_id: undefined,
          });
        }
      });

      if (cropsToUpdate.length > 0) {
        const isSuccess = await createEmbeddings(cropsToUpdate);

        if (isSuccess) {
          showSuccessToast(
            `Created ${cropsToUpdate.length} embedding${cropsToUpdate.length === 1 ? '' : 's'}`
          );
        }
      }
    } catch {}
  }, [
    token,
    imageId,
    startSession,
    detectionPogPairings,
    selectedPairingKeys,
    createEmbeddings,
    showSuccessToast,
  ]);

  const handleSave = useCallback(async () => {
    if (!productRecognitionData || !token || !taskId) {
      showErrorToast('Missing task data or authentication token');
      return;
    }

    const modifiedProductRecognitionData = applyPairingsToProductRecognitionData();
    if (!modifiedProductRecognitionData) {
      showErrorToast('Failed to prepare task data');
      return;
    }

    setIsSaveLoading(true);

    try {
      await Promise.all([
        createEmbeddingsFromPairings(),
        ApiService.createProductRecognitionFromPayload(token, modifiedProductRecognitionData),
      ]);

      // Update tasksList in localStorage
      deactivateTaskInLocalStorage(taskId);

      // Cancel other active tasks silently
      try {
        const activeTasksToCancel = getOtherActiveTaskIds(new Set([parseInt(taskId, 10)]));

        if (activeTasksToCancel.size > 0) {
          await cancelTasks(activeTasksToCancel);
        }
      } catch {}

      showSuccessToast('Changes saved. New task created.');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaveLoading(false);
    }
  }, [
    productRecognitionData,
    token,
    showErrorToast,
    showSuccessToast,
    applyPairingsToProductRecognitionData,
    createEmbeddingsFromPairings,
    taskId,
    deactivateTaskInLocalStorage,
    getOtherActiveTaskIds,
    cancelTasks,
  ]);

  const handleTaskCheckboxToggle = useCallback((taskIdToToggle: number, isActive: boolean) => {
    if (!isActive) return; // Don't allow selection of inactive tasks

    setSelectedTaskIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskIdToToggle)) {
        newSet.delete(taskIdToToggle);
      } else {
        newSet.add(taskIdToToggle);
      }
      return newSet;
    });
  }, []);

  const handleCancelTasks = useCallback(async () => {
    if (!storeName) {
      showErrorToast('Missing store name');
      return;
    }

    if (selectedTaskIds.size === 0) {
      showErrorToast('No tasks selected');
      return;
    }

    setIsCancelLoading(true);

    try {
      const { successCount, totalCount } = await cancelTasks(selectedTaskIds);
      const isSuccessfullCancelation = successCount === totalCount;

      if (isSuccessfullCancelation) {
        showSuccessToast(
          `${successCount} task${successCount > 1 ? 's' : ''} canceled successfully`
        );
      } else if (successCount > 0) {
        showErrorToast(`${successCount} of ${totalCount} tasks canceled.`);
      } else {
        showErrorToast('Failed to cancel tasks');
      }

      // Clear selection after canceling
      setSelectedTaskIds(new Set());

      const activeTaskIds = getOtherActiveTaskIds(selectedTaskIds);

      if (isSuccessfullCancelation && activeTaskIds.size === 0) {
        // Navigate to next task or go back
        if (nextTaskUrl) {
          navigate(nextTaskUrl, { replace: true });
        } else {
          navigate(-1);
        }
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to cancel tasks');
    } finally {
      setIsCancelLoading(false);
    }
  }, [
    storeName,
    selectedTaskIds,
    showErrorToast,
    cancelTasks,
    getOtherActiveTaskIds,
    showSuccessToast,
    nextTaskUrl,
    navigate,
  ]);

  // This takes the compliance action status and maps to the planogram item using il_item_id.
  // Later, I use the combination of il_item_id and status to assign the correct color to the planogram
  // item.
  const planogramStatusMap = useMemo(() => {
    const map = new Map<number, string>();
    if (planogramComplianceData && planogramComplianceData.boxes) {
      for (const box of planogramComplianceData.boxes) {
        let status = box.complianceStatus;
        map.set(box.pogItemId, status);
      }
    }
    return map;
  }, [planogramComplianceData]);

  const isFilteringByComplianceLayer = useMemo(
    () => selectedComplianceLayers.size !== Object.keys(COMPLIANCE_COLOR).length,
    [selectedComplianceLayers]
  );

  const showPogs = true;

  // Find current task
  const currentTask = useMemo(() => {
    if (!taskId) return null;
    return sortedTasks.find((t) => t.actionTaskId === parseInt(taskId, 10)) || null;
  }, [sortedTasks, taskId]);

  const currentPogs = useMemo(() => {
    const allPogs = cameraImage?.pogs ? Object.values(cameraImage.pogs) : [];

    const filteredByMod = allPogs
      .filter((pog): pog is CameraPog => !!pog?.tagName && enabledMods.has(pog.tagName))
      .sort((a, b) => {
        if (!a || !b) return 0;
        return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
      });

    return filteredByMod.map((pog) => {
      const filteredBboxes = pog.bboxes.filter((bbox) => {
        if (!bbox.ilItemId || !planogramStatusMap.has(bbox.ilItemId)) {
          return true;
        }

        // If all layers are selected, don't filter
        if (!isFilteringByComplianceLayer) {
          return true;
        }

        const status = planogramStatusMap.get(bbox.ilItemId);
        return status && selectedComplianceLayers.has(status);
      });

      return {
        ...pog,
        bboxes: filteredBboxes,
      };
    });
  }, [
    cameraImage,
    enabledMods,
    allTagNames,
    selectedComplianceLayers,
    planogramStatusMap,
    isFilteringByComplianceLayer,
  ]);

  const planogramComplianceActions = useMemo(() => {
    if (!planogramComplianceData) return [];

    const allActions = getPlanogramComplianceActions(
      planogramComplianceData,
      enabledPlanogramComplianceLayers,
      ACTION_LAYER_CONFIG,
      showProductImages,
      selectedIlItemIds,
      selectionType,
      planogramComplianceBoxClicked?.rogItemId ?? undefined,
      enabledMods
    );

    // If all layers are selected, don't filter
    if (!isFilteringByComplianceLayer) {
      return allActions;
    }

    // Filter actions by selected compliance layers
    return allActions.filter((action) => {
      const item = action.item as PlanogramComplianceBox;
      if (!item?.pogItemId) return true;

      const status = planogramStatusMap.get(item.pogItemId);
      return status && selectedComplianceLayers.has(status);
    });
  }, [
    planogramComplianceData,
    enabledPlanogramComplianceLayers,
    showProductImages,
    selectedIlItemIds,
    selectionType,
    planogramComplianceBoxClicked?.rogItemId,
    enabledMods,
    planogramStatusMap,
    selectedComplianceLayers,
    isFilteringByComplianceLayer,
  ]);

  const productRecognitionDetections = useMemo(() => {
    // Use working copy in edit mode, original productRecognitionData otherwise
    const dataSource = editMode
      ? editedProductRecognitionData || productRecognitionData
      : productRecognitionData;
    if (!dataSource) return [];

    const detections: any[] = [];

    // Add detections from each enabled layer using LAYER_CONFIG
    availableProductRecognitionLayers.forEach((layerConfig) => {
      // In edit mode, only show detections from the selected layer
      const shouldShowLayer = enabledProductRecognitionLayers.has(layerConfig.key);

      if (shouldShowLayer) {
        const layerData = dataSource[layerConfig.key as keyof ProductRecognitionResponse];
        if (Array.isArray(layerData)) {
          layerData.forEach(
            (item: ProductRecognitionBBox | ProductRecognitionPolygon, itemIndex: number) => {
              // Skip the selected detection in edit mode - it will be rendered in DragOverlay
              if (editMode && selectedDetectionIndex === itemIndex) {
                return;
              }
              if (item.shape && Array.isArray(item.shape) && item.shape.length === 4) {
                // Check if this is a polygon item
                const isPolygon =
                  'points' in item && Array.isArray(item.points) && item.points.length > 0;

                let image = null;
                if (showProductImages) {
                  image =
                    item.vizSimCandidates?.find((candidate) => candidate.isConfident)
                      ?.productImageUri || null;
                }

                // Check if we need to filter by overlap with planogramComplianceBoxClicked
                if (planogramComplianceBoxClicked && 'x1' in item) {
                  if (
                    planogramComplianceBoxClicked.x1 !== item.x1 ||
                    planogramComplianceBoxClicked.y1 !== item.y1 ||
                    planogramComplianceBoxClicked.x2 !== item.x2 ||
                    planogramComplianceBoxClicked.y2 !== item.y2
                  ) {
                    return;
                  }
                } else if (
                  'x1' in item &&
                  (selectedIlItemIds.size > 0 || isFilteringByComplianceLayer)
                ) {
                  const isNotMatchingAction = planogramComplianceActions.every((action) => {
                    const actionItem = action.item as ProductRecognitionBBox;
                    return (
                      actionItem.x1 !== item.x1 ||
                      actionItem.y1 !== item.y1 ||
                      actionItem.x2 !== item.x2 ||
                      actionItem.y2 !== item.y2
                    );
                  });

                  if (isNotMatchingAction) {
                    return;
                  }
                }

                const detectionKey = item.shape.join(',');
                const isPaired = detectionPogPairings.has(detectionKey);
                const isSelected = selectedDetectionIds.has(detectionKey);

                detections.push({
                  shape: item.shape,
                  color: isSelected ? '#eab308' : isPaired ? '#f97316' : layerConfig.bboxColor,
                  strokeWidth: 10,
                  item: item, // Include the original item data for dialog
                  isPolygon: isPolygon,
                  points: isPolygon ? item.points : undefined,
                  image,
                });
              }
            }
          );
        }
      }
    });

    return detections;
  }, [
    editMode,
    editedProductRecognitionData,
    productRecognitionData,
    availableProductRecognitionLayers,
    enabledProductRecognitionLayers,
    selectedDetectionIndex,
    showProductImages,
    planogramComplianceBoxClicked,
    selectedIlItemIds.size,
    isFilteringByComplianceLayer,
    detectionPogPairings,
    selectedDetectionIds,
    planogramComplianceActions,
  ]);

  const [showDetectionDialog, setShowDetectionDialog] = useState(false);

  const closeDetectionDialog = useCallback(() => {
    setShowDetectionDialog(false);
  }, []);

  const onProductRecognitionClick = (detection: ImageBox, detectionIndex: number) => {
    if (!detection.shape) return;

    if (editMode) {
      // In edit mode, set the clicked detection as selectedShape for removal
      setSelectedShape(detection);
      setSelectedDetectionIndex(detectionIndex);
    } else {
      // Normal mode: toggle detection in selectedDetections array
      const detectionKey = detection.shape.join(',');
      setSelectedDetections((prev) => {
        const existingIndex = prev.findIndex(
          (det) => det.shape && det.shape.join(',') === detectionKey
        );

        if (existingIndex >= 0) {
          // Remove if already selected
          return prev.filter((_, index) => index !== existingIndex);
        } else {
          // Add if not selected
          return [...prev, detection];
        }
      });
    }
  };

  const enableEditMode = useCallback(() => {
    if (editMode) {
      return;
    }

    // Entering edit mode: save current layers and clear them, create working copy
    setPreviousEnabledLayers(new Set(enabledProductRecognitionLayers));

    const availableLayers = [...enabledProductRecognitionLayers].filter((key) => {
      return key.includes('Boxes') || key === 'modPolygons' || key === 'cartonPolygons';
    });

    setEnabledProductRecognitionLayers(new Set([availableLayers[0] ?? 'productBoxes']));

    // Create a deep copy of productRecognitionData for editing
    if (productRecognitionData) {
      setEditedProductRecognitionData(JSON.parse(JSON.stringify(productRecognitionData)));
    }

    setSelectedDetections([]);
    setEditMode(true);
  }, [editMode, enabledProductRecognitionLayers, productRecognitionData]);

  const disableEditMode = useCallback(() => {
    if (!editMode) {
      return;
    }

    // Exiting edit mode: restore previous layers and reset states
    setEnabledProductRecognitionLayers(previousEnabledLayers);
    setEditMode(false);
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
    setEditedProductRecognitionData(null);
  }, [editMode, previousEnabledLayers]);

  const removeShape = useCallback(() => {
    if (
      !selectedShape ||
      !selectedShape.item ||
      !productRecognitionData ||
      !editedProductRecognitionData
    )
      return;

    const shapeToRemove = selectedShape.item;
    const updatedEditedProductRecognitionData = { ...editedProductRecognitionData };

    // Find and remove the shape from the appropriate layer
    // We know it's in first layer since that's the only layer shown in edit mode
    const selectedLayer = [...enabledProductRecognitionLayers][0];

    if (selectedLayer) {
      const layerData =
        editedProductRecognitionData[selectedLayer as keyof ProductRecognitionResponse];
      if (Array.isArray(layerData)) {
        // Filter out the shape by comparing coordinates
        const filteredData = (
          layerData as Array<ProductRecognitionBBox | ProductRecognitionPolygon>
        ).filter((item: ProductRecognitionBBox | ProductRecognitionPolygon) => {
          // Compare by shape array
          if (
            item.shape &&
            shapeToRemove.shape &&
            Array.isArray(item.shape) &&
            Array.isArray(shapeToRemove.shape)
          ) {
            return item.shape.join(',') !== shapeToRemove.shape.join(',');
          }
          return true;
        });
        (updatedEditedProductRecognitionData as any)[selectedLayer] = filteredData;
      }
    }

    // Update productRecognitionData
    setEditedProductRecognitionData(updatedEditedProductRecognitionData);
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
  }, [
    selectedShape,
    productRecognitionData,
    editedProductRecognitionData,
    enabledProductRecognitionLayers,
  ]);

  const addShape = useCallback(
    (rect: [number, number, number, number]) => {
      if (!editedProductRecognitionData) return;

      const updatedEditedProductRecognitionData = { ...editedProductRecognitionData };

      // Get the currently selected layer in edit mode
      const selectedLayer = [...enabledProductRecognitionLayers][0] as ProductRecognitionLayerType;
      const detectionType = detectionTypePerLayerType[selectedLayer];

      if (!detectionType) {
        showErrorToast(`Cannot add new detection in ${selectedLayer} layer`);
        return;
      }

      if (selectedLayer) {
        const layerData =
          editedProductRecognitionData[selectedLayer as keyof ProductRecognitionResponse];
        if (Array.isArray(layerData)) {
          // Create a new shape object matching the layer type
          const [x, y, width, height] = rect;
          const newShape: ProductRecognitionBBox = {
            shape: rect,
            x1: x,
            y1: y,
            x2: x + width,
            y2: y + height,
            confidence: 1,
            vizSimCandidates: [],
            type: detectionType,
          };

          // Add the new shape to the layer
          const updatedData = [...layerData, newShape];
          (updatedEditedProductRecognitionData as any)[selectedLayer] = updatedData;
        }
      }

      // Update editedProductRecognitionData
      setEditedProductRecognitionData(updatedEditedProductRecognitionData);
    },
    [editedProductRecognitionData, enabledProductRecognitionLayers, showErrorToast]
  );

  const handleDetectionEdited = useCallback((updatedDetection: ImageBox) => {
    // During drag: only update temporary state for visual feedback
    // This avoids expensive editedProductRecognitionData updates on every mousemove
    setSelectedShape(updatedDetection);
    setDraggedDetection(updatedDetection);
  }, []);

  const handleDragEnd = useCallback(() => {
    // On drag end: commit the final changes to editedProductRecognitionData
    if (
      !draggedDetection ||
      !editedProductRecognitionData ||
      enabledProductRecognitionLayers.size !== 1 ||
      !draggedDetection.item ||
      selectedDetectionIndex === null
    )
      return;

    const updatedEditedProductRecognitionData = { ...editedProductRecognitionData };
    const selectedLayer = [...enabledProductRecognitionLayers][0];

    // Find and update the shape in the appropriate layer
    const layerData =
      editedProductRecognitionData[selectedLayer as keyof ProductRecognitionResponse];
    if (Array.isArray(layerData)) {
      // Use selectedDetectionIndex to update the correct item directly
      const updatedData = [...layerData] as Array<
        ProductRecognitionBBox | ProductRecognitionPolygon
      >;
      if (selectedDetectionIndex >= 0 && selectedDetectionIndex < updatedData.length) {
        // Update the shape at the selected index
        if (draggedDetection.isPolygon && draggedDetection.points) {
          const polygon = updatedData[selectedDetectionIndex] as ProductRecognitionPolygon;
          polygon.points = draggedDetection.points;
          // Update shape (bounding box) and x1, y1, x2, y2
          polygon.shape = draggedDetection.shape;
          polygon.confidence = 1;
        } else {
          const bbox = updatedData[selectedDetectionIndex] as ProductRecognitionBBox;
          const [x, y, width, height] = draggedDetection.shape;
          bbox.shape = draggedDetection.shape;
          // Update x1, y1, x2, y2 from shape [x, y, width, height]
          bbox.x1 = x;
          bbox.y1 = y;
          bbox.x2 = x + width;
          bbox.y2 = y + height;
          bbox.confidence = 1;
        }
      }
      (updatedEditedProductRecognitionData as any)[selectedLayer] = updatedData;
    }

    setEditedProductRecognitionData(updatedEditedProductRecognitionData);
    setDraggedDetection(null); // Clear temporary drag state
  }, [
    draggedDetection,
    editedProductRecognitionData,
    enabledProductRecognitionLayers,
    selectedDetectionIndex,
  ]);

  const saveDetectionChanges = useCallback(() => {
    if (!editedProductRecognitionData) return;

    // Copy working data to actual productRecognitionData
    onProductRecognitionDataChange(editedProductRecognitionData);

    // Reset editing states
    setSelectedShape(null);
    disableEditMode();
  }, [disableEditMode, editedProductRecognitionData, onProductRecognitionDataChange]);

  const cancelDetectionChanges = useCallback(() => {
    // Restore editedProductRecognitionData from productRecognitionData to undo changes
    if (productRecognitionData) {
      setEditedProductRecognitionData(JSON.parse(JSON.stringify(productRecognitionData)));
    }

    disableEditMode();
  }, [disableEditMode, productRecognitionData]);

  useEscapeKey(closeDetectionDialog);

  const onBoxClick = () => {};

  const onBoxClear = useCallback(() => {
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
  }, []);

  const onProductRecognitionRightClick = (detection: ImageBox | PogBBox) => {
    setDetectionForModal(detection as ImageBox);
    setShowDetectionDialog(true);
  };

  useEffect(() => {
    setEnabledMods(new Set(allTagNames));
  }, [allTagNames]);

  // Auto-select current task when there's only one task
  useEffect(() => {
    if (sortedTasks.length === 1 && taskId) {
      const currentTask = sortedTasks[0];
      if (currentTask.isActive && currentTask.taskId === parseInt(taskId, 10)) {
        setSelectedTaskIds(new Set([currentTask.taskId]));
      }
    }
  }, [sortedTasks, taskId]);

  const toggleMod = (tagName: string) => {
    setEnabledMods((prev) => {
      const newMods = new Set(prev);
      if (newMods.has(tagName)) {
        newMods.delete(tagName);
      } else {
        newMods.add(tagName);
      }
      return newMods;
    });
  };

  const toggleComplianceLayer = (layer: string) => {
    setSelectedComplianceLayers((prev) => {
      const newLayers = new Set(prev);
      if (newLayers.has(layer)) {
        newLayers.delete(layer);
      } else {
        newLayers.add(layer);
      }
      return newLayers;
    });

    // Reset selections when toggling compliance layers
    setSelectedDetections([]);
    setSelectedIlItemIds(new Set());
    setPlanogramComplianceBoxClicked(null);
  };

  return (
    <div className="min-h-[101vh] bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1">
        {isTaskAuditMode ? (
          <TasksHeader
            tasks={sortedTasks}
            storeName={storeName}
            imageId={imageId}
            enabledMods={enabledMods}
            toggleMod={toggleMod}
            taskId={taskId}
            chainId={chainId}
            isLoading={isLoading}
          />
        ) : (
          <ImageAuditHeader
            storeName={storeName}
            imageId={imageId}
            createdAt={currentImage?.detectedAt || null}
            allTagNames={allTagNames}
            enabledMods={enabledMods}
            toggleMod={toggleMod}
            planogramComplianceData={planogramComplianceData}
          />
        )}

        {showWarning && newestTask && (
          <OldVersionWarningBanner
            newestTask={newestTask}
            currentTaskTagName={currentTask?.tagName || ''}
          />
        )}

        {productRecognitionData && preloadedImage ? (
          <>
            <CameraImageViewer
              preloadedImage={preloadedImage}
              filteredBboxes={[]}
              boxActions={[]}
              productRecognitionDetections={productRecognitionDetections}
              planogramComplianceActions={planogramComplianceActions}
              showProductImages={showProductImages}
              onBoxClick={onBoxClick}
              onProductRecognitionClick={onProductRecognitionClick}
              onProductRecognitionRightClick={onProductRecognitionRightClick}
              onPlanogramComplianceClick={onPlanogramComplianceClick}
              showPogs={showPogs}
              currentPogs={currentPogs}
              preloadedImagesB64={preloadedImagesB64}
              selectedIlItemIds={selectedIlItemIds}
              planogramStatusMap={planogramStatusMap}
              onPogBoxClick={handlePogBoxClick}
              onPogBoxRightClick={handlePogBoxRightClick}
              editMode={editMode}
              selectedShape={selectedShape}
              selectedDetectionIndex={selectedDetectionIndex}
              onEditModeEnable={enableEditMode}
              onRemoveShape={removeShape}
              onSaveChanges={saveDetectionChanges}
              onCancelChanges={cancelDetectionChanges}
              onDetectionEdited={handleDetectionEdited}
              onMouseUpWhileDragging={handleDragEnd}
              onBoxClear={onBoxClear}
              onNewShapeCreated={addShape}
              selectedComplianceLayers={selectedComplianceLayers}
              onToggleComplianceLayer={toggleComplianceLayer}
            />

            {!isTaskAuditMode && (
              <PlanogramComplianceAdvancedSettings
                config={planogramComplianceConfig}
                isV2={isV2}
                onConfigChange={(updates) =>
                  setPlanogramComplianceConfig((prev) => ({ ...prev, ...updates }))
                }
                onReset={() => setPlanogramComplianceConfig(DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG)}
              >
                <PlanogramComplianceToggles
                  config={planogramComplianceConfig}
                  onConfigChange={(updates) =>
                    setPlanogramComplianceConfig((prev) => ({ ...prev, ...updates }))
                  }
                  isV2={isV2}
                  onIsV2Change={setIsV2}
                />
              </PlanogramComplianceAdvancedSettings>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <LayersColumn
                title="Detection Layers"
                layer_config={availableProductRecognitionLayers}
                enabledLayers={enabledProductRecognitionLayers}
                setEnabledLayers={setEnabledProductRecognitionLayers}
                getLayerCount={getProductRecognitionLayerCount}
                toggleLayer={toggleProductRecognitionLayer}
              />
              <div>
                <LayersColumn
                  title="Action Layers"
                  layer_config={ACTION_LAYER_CONFIG}
                  enabledLayers={enabledPlanogramComplianceLayers}
                  setEnabledLayers={setEnabledPlanogramComplianceLayers}
                  getLayerCount={getPlanogramComplianceLayerCount}
                  toggleLayer={togglePlanogramComplianceLayer}
                />
                {planogramComplianceBoxClicked && (
                  <div className="mt-4">
                    <PlanogramComplianceActionContent
                      item={planogramComplianceBoxClicked}
                      cameraImageElement={
                        currentImage ? preloadedImages[currentImage.imageUri] : undefined
                      }
                    />
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-md p-4 lg:col-span-1 col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    Matched Detections ({detectionPogPairings.size})
                  </h3>
                  {detectionPogPairings.size > 0 && isTaskAuditMode && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {selectedPairingKeys.size} selected for embeddings
                      </span>
                      <button
                        onClick={() => {
                          if (selectedPairingKeys.size === detectionPogPairings.size) {
                            setSelectedPairingKeys(new Set());
                          } else {
                            setSelectedPairingKeys(
                              new Set(Array.from(detectionPogPairings.keys()))
                            );
                          }
                        }}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        {selectedPairingKeys.size === detectionPogPairings.size
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                  )}
                </div>
                {/* Action Buttons */}
                {
                  <div className="mb-4 pb-4 border-b space-y-6">
                    <div className="flex gap-4 justify-between">
                      <div className="flex flex-col gap-2">
                        {sortedTasks.map((task) => (
                          <div key={task.taskId} className="flex items-center gap-2">
                            {sortedTasks.length > 1 && (
                              <button
                                onClick={() => handleTaskCheckboxToggle(task.taskId, task.isActive)}
                                disabled={!task.isActive}
                                className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {selectedTaskIds.has(task.taskId) ? (
                                  <CheckBoxIcon className="text-blue-600" />
                                ) : (
                                  <CheckBoxOutlineBlankIcon className="text-gray-400" />
                                )}
                              </button>
                            )}
                            <a
                              href={task.atUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1"
                            >
                              {task.tagName} AT
                              <LaunchIcon fontSize="small" />
                            </a>
                          </div>
                        ))}
                      </div>
                      <div>
                        <button
                          onClick={handlePreviewChanges}
                          disabled={isCancelLoading || isPreviewLoading || isSaveLoading}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isPreviewLoading && <CircularProgress size={16} color="inherit" />}
                          {isPreviewLoading ? 'Previewing...' : 'Preview changes'}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-6">
                      {taskId && (
                        <>
                          <Tooltip
                            title={
                              sortedTasks.length > 1 && selectedTaskIds.size === 0
                                ? 'Select task to cancel'
                                : 'Remove task from AT'
                            }
                            arrow
                          >
                            <button
                              onClick={handleCancelTasks}
                              disabled={
                                isCancelLoading ||
                                isPreviewLoading ||
                                isSaveLoading ||
                                selectedTaskIds.size === 0
                              }
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {isCancelLoading && <CircularProgress size={16} color="inherit" />}
                              {isCancelLoading
                                ? 'Canceling...'
                                : sortedTasks.length > 1 && selectedTaskIds.size > 0
                                  ? `Cancel tasks (${selectedTaskIds.size})`
                                  : 'Cancel task'}
                            </button>
                          </Tooltip>
                          <button
                            onClick={handleSave}
                            disabled={isCancelLoading || isPreviewLoading || isSaveLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isSaveLoading && <CircularProgress size={16} color="inherit" />}

                            {isSaveLoading
                              ? 'Saving...'
                              : sortedTasks.length > 1
                                ? `Save tasks (${sortedTasks.length})`
                                : 'Save task'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                }

                {detectionPogPairings.size === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No detections matched yet. Select a detection box on the camera image, then
                    click a POG box to match them.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {Array.from(detectionPogPairings.values()).map((pairing) => {
                      const detectionKey = getDetectionKey(pairing.detection);
                      const cameraImageElement = currentImage
                        ? preloadedImages[currentImage.imageUri]
                        : undefined;

                      return (
                        <div
                          key={detectionKey}
                          className="flex gap-4 p-4 border rounded-lg bg-gray-50 relative"
                        >
                          {/* Before (Detection) */}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              Before (Detection)
                            </div>
                            <div className="bg-white rounded border p-2 mb-2">
                              {cameraImageElement && pairing.detection.shape ? (
                                <BoxedImage
                                  image={cameraImageElement}
                                  crop={pairing.detection.shape}
                                  cropPadding={20}
                                  boxes={[
                                    {
                                      shape: pairing.detection.shape,
                                      color: pairing.detection.color || '#3b82f6',
                                      strokeWidth: 5,
                                      isPolygon: pairing.detection.isPolygon,
                                      points: pairing.detection.points,
                                    },
                                  ]}
                                />
                              ) : (
                                <div className="w-full h-32 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="text-xs space-y-1">
                              <div>
                                <span className="font-medium">UPC:</span>{' '}
                                {pairing.detection.item?.vizSimCandidates?.[0]?.normalizedUpc ||
                                  'N/A'}
                              </div>
                            </div>
                          </div>

                          {/* Arrow */}
                          <div className="flex items-center justify-center px-2">
                            <svg
                              className="w-6 h-6 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                              />
                            </svg>
                          </div>

                          {/* After (Matched Product) */}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              After ({pairing.source === 'pog' ? 'POG' : 'Manual'})
                            </div>
                            <div className="bg-white rounded border p-2 mb-2">
                              {pairing.source === 'pog' && pairing.pogBox?.imageUri ? (
                                <img
                                  src={pairing.pogBox.imageUri}
                                  alt={pairing.name || pairing.upc}
                                  className="w-full h-32 object-contain"
                                />
                              ) : pairing.source === 'manual' && pairing.imageUri ? (
                                <img
                                  src={pairing.imageUri}
                                  alt={pairing.name || pairing.upc}
                                  className="w-full h-32 object-contain"
                                />
                              ) : (
                                <div className="w-full h-32 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="text-xs space-y-1">
                              <div>
                                <span className="font-medium">UPC:</span> {pairing.upc}
                              </div>
                              <div>
                                <span className="font-medium">Name:</span> {pairing.name || 'N/A'}
                              </div>
                            </div>
                          </div>

                          {/* Checkbox */}
                          {isTaskAuditMode && (
                            <div className="flex items-center pt-1">
                              <input
                                type="checkbox"
                                checked={selectedPairingKeys.has(detectionKey)}
                                onChange={(e) => {
                                  setSelectedPairingKeys((prev) => {
                                    const newSet = new Set(prev);
                                    if (e.target.checked) {
                                      newSet.add(detectionKey);
                                    } else {
                                      newSet.delete(detectionKey);
                                    }
                                    return newSet;
                                  });
                                }}
                                className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                title="Select for embedding creation"
                              />
                            </div>
                          )}

                          {/* Delete button */}
                          <div className="flex items-start">
                            <button
                              onClick={() => {
                                setDetectionPogPairings((prev) => {
                                  const newMap = new Map(prev);
                                  newMap.delete(detectionKey);
                                  return newMap;
                                });
                                // Also remove from selection
                                setSelectedPairingKeys((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(detectionKey);
                                  return newSet;
                                });
                              }}
                              className="absolute z-2 top-0.5 right-0.5 p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove pairing"
                            >
                              <DeleteOutlineIcon />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div>Loading image</div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-blue-500">
                  <CircularProgress size={32} color="inherit" />
                </div>
                <p className="text-gray-500">Loading...</p>
              </div>
            </div>
          ) : (
            !productRecognitionData && (
              <p className="text-gray-500 text-center">No data available</p>
            )
          )}
        </div>
      </div>

      {/* Detection Details Dialog */}
      {detectionForModal && (
        <ImageItemDetailsDialog
          item={detectionForModal.item}
          cameraImageElement={currentImage ? preloadedImages[currentImage.imageUri] : undefined}
          isOpen={showDetectionDialog}
          onClose={closeDetectionDialog}
          layerBboxColor={detectionForModal.color}
          chainId={chainId ? Number.parseInt(chainId) : undefined}
          storeId={storeId ? Number.parseInt(storeId) : undefined}
          onUpcSubmit={handleManualUpcSubmit}
          shouldShowEmbeddings={true}
        />
      )}
    </div>
  );
};
